/**
 * A shelf's own page — artwork, the shelf's intro, and everything on it.
 *
 * Reached from the artwork rail on the marketplace home and from the compact shelf pills in the
 * header. Clicking a shelf now lands somewhere, which is the whole point.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "./api";
import { MarketplaceHeader } from "../../components/marketplace/MarketplaceHeader";
import { AddedToCartOverlay } from "../../components/marketplace/AddedToCartOverlay";
import { ProductCard } from "../../components/marketplace/ProductCard";
import { MarketplacePagination } from "../../components/marketplace/MarketplacePagination";
import "./MarketplaceHome.css";
import { cssUrl } from '@/lib/cssUrl';

const SORT_OPTIONS = [
  { value: "mix", label: "Fresh mix" },
  { value: "newest", label: "Newest first" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "rating", label: "Best rated" },
  { value: "name", label: "Name A–Z" },
];

export default function ShelfPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem("token");

  const [shelf, setShelf] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sort, setSort] = useState("mix");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  const [cartLines, setCartLines] = useState([]);
  const [addedProduct, setAddedProduct] = useState(null);
  const [savedIds, setSavedIds] = useState(() => new Set());

  useEffect(() => {
    setNotFound(false);
    window.scrollTo({ top: 0 });
    document.querySelector('.dashboard-scroll, main')?.scrollTo?.({ top: 0 });
    apiFetch(`/marketplace/shelves/${slug}`)
      .then(setShelf)
      .catch(() => setNotFound(true));
    setPage(1);
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 15, shelf: slug, sort });
    if (minPrice !== "") params.set("min_price", minPrice);
    if (maxPrice !== "") params.set("max_price", maxPrice);
    apiFetch(`/marketplace/products?${params}`)
      .then((d) => {
        setProducts(d.products || []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 1);
      })
      .catch(() => { setProducts([]); setTotal(0); setTotalPages(1); })
      .finally(() => setLoading(false));
  }, [slug, page, sort, minPrice, maxPrice]);

  const loadCartLines = useCallback(() => {
    apiFetch("/cart").then((c) => setCartLines(c.items || [])).catch(() => setCartLines([]));
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    loadCartLines();
    apiFetch("/wishlist/ids").then((d) => setSavedIds(new Set(d.product_ids || []))).catch(() => {});
  }, [isLoggedIn, loadCartLines]);

  const qtyInCart = (id) => (cartLines.find((i) => i.product_id === id)?.qty || 0);

  const addToCart = async (product) => {
    try {
      await apiFetch("/cart/items", {
        method: "POST", body: JSON.stringify({ product_id: product.id, qty: 1 }),
      });
      setAddedProduct(product);
    } catch (e) {
      toast.error(e.message || "Could not add that to your basket");
    }
    loadCartLines();
    window.dispatchEvent(new Event("cart-updated"));
  };

  const changeQty = async (product, qty) => {
    if (qty <= 0) await apiFetch(`/cart/items/${product.id}`, { method: "DELETE" }).catch(() => {});
    else await apiFetch(`/cart/items/${product.id}`, {
      method: "PATCH", body: JSON.stringify({ qty }),
    }).catch(() => {});
    loadCartLines();
    window.dispatchEvent(new Event("cart-updated"));
  };

  const toggleSave = async (product) => {
    const wasSaved = savedIds.has(product.id);
    setSavedIds((prev) => {
      const next = new Set(prev);
      wasSaved ? next.delete(product.id) : next.add(product.id);
      return next;
    });
    await apiFetch(`/wishlist/${product.id}`, { method: wasSaved ? "DELETE" : "POST" })
      .catch(() => {});
  };

  if (notFound) {
    return (
      <div className="mp-root">
        <MarketplaceHeader />
        <div className="mp-container" style={{ padding: 40, textAlign: "center" }}
          data-testid="shelf-not-found">
          <p className="mp-title">That shelf is not here</p>
          <button className="mp-nav-btn" onClick={() => navigate("/dashboard/marketplace")}>
            Back to the Marketplace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-root" data-testid="shelf-page">
      <MarketplaceHeader activeShelf={slug} savedCount={savedIds.size} />

      <div className="mp-container">
        <button className="mp-shelf-back" onClick={() => navigate("/dashboard/marketplace")}
          data-testid="shelf-back-btn">
          <ArrowLeft size={14} /> Marketplace
        </button>

        <div
          className="mp-shelf-hero"
          style={shelf?.artwork_url ? { backgroundImage: cssUrl(shelf.artwork_url) } : undefined}
          data-testid="shelf-hero"
        >
          <div className="mp-shelf-hero-inner">
            <p className="mp-shelf-hero-eyebrow">Shelf</p>
            <h1 className="mp-shelf-hero-title" data-testid="shelf-title">
              {shelf?.name || "…"}
            </h1>
            {shelf?.intro && (
              <p className="mp-shelf-hero-intro" data-testid="shelf-intro">{shelf.intro}</p>
            )}
          </div>
        </div>

        <div className="mp-sortbar" data-testid="shelf-sortbar">
          <label htmlFor="shelf-sort">Sort</label>
          <select id="shelf-sort" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}
            data-testid="shelf-sort-select">
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <label htmlFor="shelf-min">Price</label>
          <input id="shelf-min" type="number" min="0" placeholder="Min" value={minPrice}
            onChange={(e) => { setMinPrice(e.target.value); setPage(1); }} data-testid="shelf-min-price" />
          <span style={{ color: "rgba(245,240,232,0.35)" }}>–</span>
          <input type="number" min="0" placeholder="Max" value={maxPrice}
            onChange={(e) => { setMaxPrice(e.target.value); setPage(1); }} data-testid="shelf-max-price" />
          <span className="mp-sortbar-count">{total} product{total !== 1 ? "s" : ""}</span>
        </div>

        <div className="mp-section">
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(245,240,232,0.4)" }}>
              Loading…
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(245,240,232,0.4)" }}
              data-testid="shelf-empty">
              Nothing on this shelf yet. New pieces land here as vendors list them.
            </div>
          ) : (
            <div className="mp-product-grid">
              {products.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  saved={savedIds.has(p.id)}
                  onToggleSave={isLoggedIn ? toggleSave : undefined}
                  cartQty={qtyInCart(p.id)}
                  onAdd={isLoggedIn ? addToCart : undefined}
                  onQty={isLoggedIn ? changeQty : undefined}
                  onClick={() => navigate(`/dashboard/marketplace/product/${p.id}`)}
                />
              ))}
            </div>
          )}

          <MarketplacePagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>

        <div style={{ height: 24 }} />
      </div>

      {addedProduct && (
        <AddedToCartOverlay
          product={addedProduct}
          onClose={() => { setAddedProduct(null); loadCartLines(); }}
        />
      )}
    </div>
  );
}
